/**
 * Shared admin page-numbered pagination footer.
 *
 * Wraps `@bravemove/ui-web`'s `<Pagination>` (page numbers + prev/next) with a
 * "Page X of Y" / "Showing N of M" summary, using the shared
 * `admin:common.pagination.*` i18n keys. Replaces the legacy cursor-based
 * "Load more" button across the admin list tables.
 *
 * Renders nothing when there is at most one page AND nothing to summarise.
 */
import { Pagination } from '@bravemove/ui-web';
import { useI18n } from '@bravemove/i18n/react';
import type { AdminPageMeta } from '@bravemove/types';

export interface AdminPaginationProps {
  /** Page-numbered meta from the list endpoint (`page`/`perPage`/`total`/`totalPages`). */
  readonly pagination: AdminPageMeta;
  /** Called with the 1-based target page when the user navigates. */
  readonly onChange: (page: number) => void;
  /** Number of rows currently rendered (for the "Showing N of M" summary). */
  readonly shownCount: number;
  readonly className?: string;
  /** Optional test id forwarded to the wrapper for E2E/unit assertions. */
  readonly testId?: string;
}

export function AdminPagination({
  pagination,
  onChange,
  shownCount,
  className,
  testId = 'admin-pagination',
}: AdminPaginationProps) {
  const { t } = useI18n();
  const { page, total, totalPages } = pagination;

  // Hide entirely when there's a single page of (or no) results.
  if (totalPages <= 1 && total <= shownCount) return null;

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 ${className ?? ''}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 text-[12px] text-ink-3">
        <span data-testid={`${testId}-page-of`}>
          {t('admin:common.pagination.pageOf', {
            page,
            totalPages,
            defaultValue: `Page ${page} of ${totalPages}`,
          })}
        </span>
        <span aria-hidden="true">·</span>
        <span data-testid={`${testId}-showing`}>
          {t('admin:common.pagination.showing', {
            count: shownCount,
            total,
            defaultValue: `${shownCount} of ${total}`,
          })}
        </span>
      </div>
      <Pagination
        page={page}
        pageCount={totalPages}
        onChange={onChange}
        ariaLabel={t('admin:common.pagination.aria', { defaultValue: 'Pagination' })}
      />
    </div>
  );
}
